import React from "react";
import { Sparkles, ArrowUpRight, TrendingUp, Users } from "lucide-react";
import { t } from "@/lib/i18n";

const TIER_LABEL_KEY = {
  scout: "tier_scout",
  refer_close: "tier_refer_close",
  operator: "tier_operator",
};

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function TierCard({ lang, tier, nextTier, gapUsd, gapHint, passiveEarnings, downlineCount, downlineSigned }) {
  if (!tier) return null;
  const tierKey = TIER_LABEL_KEY[tier.id] || "tier_scout";

  return (
    <section
      className="relative eigoo-card overflow-hidden p-6 sm:p-7 anim-fade-up anim-delay-1"
      data-testid="tier-card"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#d4a843]/10 blur-3xl pointer-events-none" />
      <div className="grid lg:grid-cols-5 gap-6 relative">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d4a843]/30 bg-[#0f2038] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#f3c669]">
            <Sparkles className="w-3 h-3" /> {t(lang, "current_tier")}
          </div>
          <h3
            className="font-display mt-3 text-3xl sm:text-4xl font-extrabold gold-gradient-text"
            data-testid="tier-name"
          >
            {t(lang, tierKey)}
          </h3>
          <div className="mt-3 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-2xl font-bold text-white tabular-nums" data-testid="tier-active-rate">
              {tier.active_rate_pct}%
            </span>
            <span className="text-xs uppercase tracking-widest text-slate-400">
              {t(lang, "active_rate")}
            </span>
            <span className="mx-1 text-slate-600">·</span>
            <span className="font-mono text-2xl font-bold text-white tabular-nums" data-testid="tier-passive-rate">
              {tier.passive_rate_pct}%
            </span>
            <span className="text-xs uppercase tracking-widest text-slate-400">
              {t(lang, "passive_rate")}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-3 max-w-sm">
            {t(lang, "tier_disclaimer")}
          </p>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-[#1e3a61] bg-[#0f2038]/60 p-5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <Users className="w-3.5 h-3.5" /> {t(lang, "passive_from_downline")}
          </div>
          <div className="font-display mt-1.5 text-2xl sm:text-3xl font-extrabold text-white tabular-nums" data-testid="passive-earnings">
            {fmtUSD(passiveEarnings)}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-[#0a1628]/70 border border-[#1e3a61] px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{t(lang, "downline_count")}</div>
              <div className="font-display text-lg font-bold text-white tabular-nums">{downlineCount ?? 0}</div>
            </div>
            <div className="rounded-lg bg-[#0a1628]/70 border border-[#1e3a61] px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{t(lang, "downline_signed")}</div>
              <div className="font-display text-lg font-bold text-white tabular-nums">{downlineSigned ?? 0}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 rounded-2xl border border-[#d4a843]/30 bg-[#d4a843]/[0.06] p-5 flex flex-col">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f3c669]">
            <TrendingUp className="w-3.5 h-3.5" /> {t(lang, "next_tier")}
          </div>
          {nextTier ? (
            <>
              <div className="font-display mt-1.5 text-xl font-extrabold text-white">
                {t(lang, TIER_LABEL_KEY[nextTier.id] || "tier_refer_close")}
              </div>
              <div className="mt-1 text-[11px] font-mono text-[#f3c669]">
                {nextTier.active_pct}% / {nextTier.passive_pct}%
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">
                {t(lang, "youre_missing")}
              </div>
              <div
                className="font-display text-2xl font-extrabold gold-gradient-text tabular-nums"
                data-testid="tier-gap-usd"
              >
                {fmtUSD(gapUsd)}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{gapHint}</div>
              <div className="mt-auto pt-3 inline-flex items-center gap-1.5 text-[11px] text-[#f3c669]">
                <ArrowUpRight className="w-3.5 h-3.5" /> {t(lang, "ask_upgrade")}
              </div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-300">
              {t(lang, "top_tier_reached")}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
